import { appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// FeedbackOutcomePublic.js
// =============================================================================
// The page opened from "Click here to record outcome of complaint" in the
// branch manager's complaint-copy email (and every later reminder email):
// "/?fbo=TOKEN". No login, no nav rail — same shape as PatientFeedbackPublic's
// "?pfb=TOKEN" and the other public token-gated pages (see App.js for the
// early-return wiring this mirrors).
//
// Deliberately self-contained, same reasoning as PatientFeedbackPublic.js:
// this is a public, unauthenticated page (the token is the credential), so
// it has no dependency on anything that talks to a manager's login token.
//
// Shows the complaint as submitted, then a small form for the outcome:
// response to patient, response date/time, investigation outcome, action
// taken, and a "superintendent to be informed" checkbox. "Save changes"
// keeps it open for further edits; "Save changes and close complaint" asks
// for confirmation, then locks it — the manager (or any later reload of
// this same link) sees a read-only summary from then on.
// =============================================================================

import React, { useEffect, useState } from "react";
const API_BASE = apiUrl("");
const S = {
  page: {
    minHeight: "100vh",
    background: "#F5F5F5",
    fontFamily: "system-ui, sans-serif",
    padding: "32px 16px",
    display: "flex",
    justifyContent: "center"
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    width: 560,
    maxWidth: "100%",
    padding: 28,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)"
  },
  h1: {
    fontSize: 18,
    fontWeight: 700,
    color: "#262626",
    margin: "0 0 4px"
  },
  sub: {
    fontSize: 12.5,
    color: "#5D5D5D",
    margin: "0 0 18px"
  },
  detailBox: {
    background: "#FAFAFA",
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    padding: "12px 14px",
    marginBottom: 20
  },
  detailRow: {
    fontSize: 13,
    color: "#434343",
    marginTop: 4
  },
  label: {
    fontSize: 12.5,
    fontWeight: 700,
    color: "#5D5D5D",
    display: "block",
    marginBottom: 6,
    marginTop: 14
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 11px",
    fontSize: 13.5,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontFamily: "inherit"
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 11px",
    fontSize: 13.5,
    minHeight: 90,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontFamily: "inherit",
    resize: "vertical"
  },
  btn: {
    padding: "11px 18px",
    fontSize: 14,
    fontWeight: 700,
    border: "none",
    borderRadius: 8,
    background: "#722ED1",
    color: "#fff",
    cursor: "pointer"
  },
  btnDanger: {
    padding: "11px 18px",
    fontSize: 14,
    fontWeight: 700,
    border: "none",
    borderRadius: 8,
    background: "#CF1322",
    color: "#fff",
    cursor: "pointer"
  },
  ok: {
    fontSize: 14,
    color: "#389E0D",
    background: "#F6FFED",
    border: "1px solid #B7EB8F",
    borderRadius: 8,
    padding: "14px 16px",
    marginTop: 16,
    textAlign: "center"
  },
  err: {
    fontSize: 13,
    color: "#A8071A",
    background: "#FFF1F0",
    border: "1px solid #FFCCC7",
    borderRadius: 6,
    padding: "9px 12px",
    marginTop: 12
  },
  closedBadge: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    color: "#434343",
    background: "#F0F0F0",
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    padding: "2px 8px",
    marginLeft: 8
  }
};
export default function FeedbackOutcomePublic({
  token
}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [complaint, setComplaint] = useState(null); // { pharmacyName, submittedAt, customerName, customerEmail, comments, rating, outcome, closed }

  const [responseToPatient, setResponseToPatient] = useState("");
  const [responseDateTime, setResponseDateTime] = useState("");
  const [investigationOutcome, setInvestigationOutcome] = useState("");
  const [actionTaken, setActionTaken] = useState("");
  const [superintendentInformed, setSuperintendentInformed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [justClosed, setJustClosed] = useState(false);
  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API_BASE}/api/feedback/outcome/site?token=${encodeURIComponent(token)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "This link isn't valid.");
      setComplaint(data);
      const o = data.outcome || {};
      setResponseToPatient(o.responseToPatient || "");
      setResponseDateTime(o.responseDateTime || "");
      setInvestigationOutcome(o.investigationOutcome || "");
      setActionTaken(o.actionTaken || "");
      setSuperintendentInformed(!!o.superintendentInformed);
    } catch (e) {
      setLoadError(e.message);
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async close => {
    if (close) {
      if (!(await appConfirm("This will save this complaint and further changes cannot be made. Continue?"))) return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/feedback/outcome/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          token,
          responseToPatient,
          responseDateTime,
          investigationOutcome,
          actionTaken,
          superintendentInformed,
          close: !!close
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Something went wrong saving this.");
      if (close) {
        setJustClosed(true);
      } else {
        setSaveMsg({
          type: "ok",
          text: "Saved."
        });
      }
      await load();
    } catch (e) {
      setSaveMsg({
        type: "err",
        text: e.message
      });
    }
    setSaving(false);
  };
  if (loading) {
    return <div style={S.page}><div style={S.card}>Loading…</div></div>;
  }
  if (loadError || !complaint) {
    return <div style={S.page}>
        <div style={S.card}>
          <div style={{
          fontWeight: 700,
          fontSize: 16,
          marginBottom: 6
        }}>This link isn't valid</div>
          <div style={{
          fontSize: 13.5,
          color: "#5D5D5D"
        }}>{loadError || "Please ask for a fresh link."}</div>
        </div>
      </div>;
  }
  const closed = !!complaint.closed;
  return <div style={S.page}>
      <div style={S.card}>
        <div style={S.h1}>
          Record outcome of complaint
          {closed && <span style={S.closedBadge}>CLOSED</span>}
        </div>
        <div style={S.sub}>{complaint.pharmacyName || "Pharmacy"}</div>

        <div style={S.detailBox}>
          <div style={{
          fontSize: 11.5,
          fontWeight: 700,
          color: "#5D5D5D",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}>
            Complaint details
          </div>
          <div style={S.detailRow}>
            <b>Received:</b> {complaint.submittedAt ? new Date(complaint.submittedAt).toLocaleString() : "—"}
          </div>
          {(complaint.customerName || complaint.customerEmail) && <div style={S.detailRow}>
              <b>From:</b> {complaint.customerName || "—"} {complaint.customerEmail ? `<${complaint.customerEmail}>` : ""}
            </div>}
          {complaint.comments && <div style={{
          ...S.detailRow,
          whiteSpace: "pre-wrap"
        }}>
              <b>Complaint:</b> {complaint.comments}
            </div>}
        </div>

        {justClosed && <div style={S.ok}>This complaint has been saved and closed. No further changes can be made.</div>}

        {!justClosed && <>
            <label style={S.label}>Response to patient:</label>
            <textarea style={S.textarea} value={responseToPatient} disabled={closed} onChange={e => setResponseToPatient(e.target.value)} />

            <label style={S.label}>Response date/time:</label>
            <input style={S.input} value={responseDateTime} disabled={closed} onChange={e => setResponseDateTime(e.target.value)} placeholder="e.g. 15/09/2026 14:30" />

            <label style={S.label}>Investigation outcome:</label>
            <textarea style={S.textarea} value={investigationOutcome} disabled={closed} onChange={e => setInvestigationOutcome(e.target.value)} />

            <label style={S.label}>Action taken:</label>
            <textarea style={S.textarea} value={actionTaken} disabled={closed} onChange={e => setActionTaken(e.target.value)} />

            <label style={{
          fontSize: 13.5,
          color: "#434343",
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 16
        }}>
              <input type="checkbox" checked={superintendentInformed} disabled={closed} onChange={e => setSuperintendentInformed(e.target.checked)} />
              Superintendent to be informed?
            </label>

            {closed ? <div style={S.ok}>This complaint was saved and closed — no further changes can be made.</div> : <div style={{
          display: "flex",
          gap: 10,
          marginTop: 22,
          flexWrap: "wrap"
        }}>
                <button style={S.btn} onClick={() => save(false)} disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </button>
                <button style={S.btnDanger} onClick={() => save(true)} disabled={saving}>
                  {saving ? "Saving…" : "Save changes and close complaint"}
                </button>
              </div>}
            {saveMsg && <div style={saveMsg.type === "err" ? S.err : S.ok}>{saveMsg.text}</div>}
          </>}
      </div>
    </div>;
}
